import React from "react";
import {doppeActionTypeDefineViewerMetadata} from "../../client-server-common/doppe-action-types/doppe-action-type";
import {doppeActionTypeLink} from "./doppe-action-type-link";
import {
    DoppeActionCTATypeLinkOpenType,
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl,
    DoppeLinkSpecEntityType
} from "../../client-server-common/types/doppe-action-cta-button";
import {
    bymoPageGetInnerNavigatablePageById,
    DoppeDtoInnerPageType
} from "../../client-server-common/types/doppe-dto-inner-page";
import {DoppeViewerModalPanelLayout} from "../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel";
import {
    doppeViewerActionContextOpenModal
} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils";
import {
    DoppeActionModalIframeContent
} from "../../components/doppe-action-modal-iframe-content/doppe-action-modal-iframe-content";
import type {IDoppeViewerActionContext} from "../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context";

/**
 * Helper function to navigate to a page using the router.
 * Works in both LivePreview and Live modes.
 */
function navigateToPageViaRouter(
    pageId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices, bymoPageProps}) => {
        const router = clientServices.viewerRouterClientService;
        const page = bymoPageGetInnerNavigatablePageById(pageId, bymoPageProps);
        
        if (page) {
            switch (page.type) {
                case DoppeDtoInnerPageType.Products:
                    router.navigateToProductsList();
                    break;
                case DoppeDtoInnerPageType.BlogPosts:
                    router.navigateToBlogPostsList();
                    break;
                case DoppeDtoInnerPageType.Events:
                    router.navigateToEventsList();
                    break;
                case DoppeDtoInnerPageType.Services:
                    router.navigateToServicesList();
                    break;
                case DoppeDtoInnerPageType.Content:
                default:
                    router.navigateToContentPage(page.slug);
                    break;
            }
        }
    });
}

/**
 * Helper function to navigate to an entity item page using the router.
 * Works in both LivePreview and Live modes.
 */
function navigateToEntityViaRouter(
    entityType: DoppeLinkSpecEntityType,
    entityId: string,
    actionContext: IDoppeViewerActionContext<any>
) {
    actionContext.performContextCallback(async ({clientServices}) => {
        const router = clientServices.viewerRouterClientService;
        
        switch (entityType) {
            case DoppeLinkSpecEntityType.Product:
                router.navigateToProductItem(entityId);
                break;
            case DoppeLinkSpecEntityType.BlogPost:
                router.navigateToBlogPostItem(entityId);
                break;
            case DoppeLinkSpecEntityType.Event:
                router.navigateToEventItem(entityId);
                break;
            case DoppeLinkSpecEntityType.Service:
                router.navigateToServiceItem(entityId);
                break;
        }
    });
}

export const doppeActionTypeLinkViewer = doppeActionTypeDefineViewerMetadata(doppeActionTypeLink, {
    getActionButtonProps: actionContext => {

        const action = actionContext.action;
        const url = action.settings.url;
        const openType = action.settings.openType;
        
        // For Page link specs, always navigate via router (same tab)
        // openType is ignored for non-URL link specs
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                return {
                    onClick: () => {
                        navigateToPageViaRouter(pageId, actionContext);
                    }
                };
            }
        }
        
        // For Entity link specs (catalog items), navigate to entity item page via router
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                return {
                    onClick: () => {
                        navigateToEntityViaRouter(entityType, entityId, actionContext);
                    }
                };
            }
        }
        
        // For URL links only: handle openType
        if (doppeLinkSpecIsUrl(url)) {
            // For InPage (popup) mode, show content in modal
            if (openType === DoppeActionCTATypeLinkOpenType.InPage) {
                return {
                    onClick: () => {
                        // Use the same modal pattern as CTA buttons - with header and close button
                        doppeViewerActionContextOpenModal(
                            actionContext,
                            () => ({
                                render: () => (
                                    <DoppeActionModalIframeContent
                                        withMinHeight={false}
                                        src={url}
                                    />
                                ),
                                dependencies: []
                            }),
                            {
                                mobileLayout: DoppeViewerModalPanelLayout.InnerFull,
                                desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                                showHeader: true,
                                showHeaderAsCloseButton: false,
                                title: action.name
                            }
                        );
                    }
                };
            }
            
            // For URL with NewTab or SameTab, use standard href approach
            const target = openType === DoppeActionCTATypeLinkOpenType.NewTab ? '_blank' : '_self';
            
            return {
                href: url,
                target: target,
                rel: openType === DoppeActionCTATypeLinkOpenType.NewTab ? 'noopener' : undefined,
            };
        }

        // Fallback: no url set
        return {};
    },
    
    activateCTA: actionContext => {
        const action = actionContext.action;
        const url = action.settings.url;
        const openType = action.settings.openType;
        
        // For Page link specs, always navigate via router (same tab)
        // openType is ignored for non-URL link specs
        if (doppeLinkSpecIsPage(url)) {
            const pageId = url.pageId;
            if (pageId) {
                navigateToPageViaRouter(pageId, actionContext);
            }
            return;
        }
        
        // For Entity link specs (catalog items), navigate to entity item page via router
        if (doppeLinkSpecIsEntity(url)) {
            const entityType = url.entityType;
            const entityId = url.entityId;
            if (entityType && entityId) {
                navigateToEntityViaRouter(entityType, entityId, actionContext);
            }
            return;
        }
        
        // For URL links only: handle openType
        if (doppeLinkSpecIsUrl(url)) {
            // For InPage (popup) mode, show content in modal with header and close button
            if (openType === DoppeActionCTATypeLinkOpenType.InPage) {
                doppeViewerActionContextOpenModal(
                    actionContext,
                    () => ({
                        render: () => (
                            <DoppeActionModalIframeContent
                                withMinHeight={false}
                                src={url}
                            />
                        ),
                        dependencies: []
                    }),
                    {
                        mobileLayout: DoppeViewerModalPanelLayout.InnerFull,
                        desktopLayout: DoppeViewerModalPanelLayout.InnerFull,
                        showHeader: true,
                        showHeaderAsCloseButton: false,
                        title: action.name
                    }
                );
                return;
            }
            
            // For SameTab or NewTab URL links
            const newTab = openType === DoppeActionCTATypeLinkOpenType.NewTab;
            actionContext.navigateToUrl(url, newTab);
        }
    }
})